import { Pack, Repack, HeroBannerContentType } from 'types/generated/graphql';
import { BaseMeta } from 'types';
import { Pack as LegacyPackTyping } from 'types';
import { PackPlayButtonClickTrackerV2Type } from 'contexts/TrackingContext/TrackingContext.types';
import { InteractionSource } from 'utils/analytics/AnalyticsV2Service.generated';
import { SlideProps, Artwork, SlideDetails } from '../types';
import { getFallbackByline } from '../helpers/getFallbackByline';
import { getSlideArtworks } from '../helpers/getSlideArtworks';
import getTypeFromContentType from '../helpers/getTypeFromContentType';

const useSlide = (
    slide: SlideProps,
    getPackPlayButtonClickTrackerV2: PackPlayButtonClickTrackerV2Type,
): {
    isLoading: boolean;
    artworks?: [Artwork, Artwork, Artwork];
    backgroundColor?: string;
    foregroundColor?: string;
    details?: SlideDetails;
} => {
    const {
        isLoading = false,
        contentType,
        entity,
        byline,
        title,
        description,
        tags = [],
        labelPacks,
    } = slide;

    const isLabel = contentType === HeroBannerContentType.Label;

    const firstPacks = (labelPacks && labelPacks[0]) ?? undefined;
    const secondPacks = (labelPacks && labelPacks[1]) ?? undefined;
    const thirdPacks = (labelPacks && labelPacks[2]) ?? undefined;

    const type = contentType && getTypeFromContentType(contentType);

    const backgroundColor = isLabel
        ? firstPacks?.backgroundColor
        : (entity as Pack | Repack)?.backgroundColor;

    const foregroundColor = isLabel
        ? firstPacks?.foregroundColor
        : (entity as Pack | Repack)?.foregroundColor;

    const fallbackByline = getFallbackByline(entity, contentType);
    const fallbackTile = entity?.name;
    const fallbackDescription = entity?.description;
    const slideArtworks = isLabel
        ? getSlideArtworks([firstPacks, secondPacks, thirdPacks])
        : getSlideArtworks(entity as Pack | Repack);
    const demoGuid = isLabel ? firstPacks?.guid : entity?.guid;

    const entityTags = [
        (entity as Pack | Repack)?.mainGenre,
        ...((entity as Pack | Repack)?.genres ?? []),
        (entity as Repack)?.instrument,
        ...((entity as Pack | Repack)?.sfxs ?? []),
    ] as BaseMeta[];

    const combinedTags = [...tags, ...entityTags].filter((tag) => tag);

    const playDemoCallback = (totalTime: number) => {
        getPackPlayButtonClickTrackerV2({
            interactionSource: InteractionSource.Banner,
            item: entity as unknown as LegacyPackTyping, // https://mixgenius.atlassian.net/browse/SP-8405
        })(totalTime);
    };

    const details = {
        tags: combinedTags,
        type: type,
        demoGuid: demoGuid,
        byline: byline ?? fallbackByline,
        title: title || fallbackTile || undefined,
        description: description || fallbackDescription || undefined,
        slug: entity?.slug || undefined,
        playDemoCallback: playDemoCallback,
    };

    return {
        isLoading,
        artworks: slideArtworks,
        backgroundColor: backgroundColor ?? undefined,
        foregroundColor: foregroundColor ?? undefined,
        details,
    };
};

export default useSlide;
